import { API_BASE_URL } from '@/lib/env'
import { ApiError } from './errors'
import { type ApiEnvelope } from './types'

/** Backend base URL (BFF). Empty string = same origin. See src/lib/env.ts. */
export const API_BASE: string = API_BASE_URL

export type QueryParams = Record<string, string | number | boolean | null | undefined>

export interface ApiFetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  query?: QueryParams
  headers?: Record<string, string>
  /** Idempotency-Key for mutating POSTs (§5.4) — pass the same key on retries. */
  idempotencyKey?: string
  /** If-Match ETag for optimistic-locking mutations (§5.5). */
  ifMatch?: string
  signal?: AbortSignal
  /** Internal: set when retrying after a silent session refresh. */
  _isRetry?: boolean
}

export interface ApiResult<T> {
  data: T
  /** Human message from the envelope, when the server sent one. */
  message: string | null
  /** ETag response header for optimistic-locking flows. */
  etag: string | null
  status: number
}

export function buildUrl(path: string, query?: QueryParams): string {
  const url = new URL(API_BASE + path, window.location.origin)
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value))
      }
    }
  }
  return url.toString()
}

let refreshInFlight: Promise<boolean> | null = null

/** Single-flight silent refresh; resolves false when the session is gone for good. */
async function trySilentRefresh(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(buildUrl('/api/v1/auth/refresh'), {
        method: 'POST',
        credentials: 'include',
      })
      return res.ok
    } catch {
      return false
    } finally {
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}

/**
 * The one fetch wrapper (UI-RECOMMENDATIONS §3): sends the session cookie,
 * unwraps the response envelope, and throws a typed ApiError on failure.
 * On 401 it attempts one silent refresh, then retries the request once.
 */
export async function apiFetchWithMeta<T>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<ApiResult<T>> {
  const { method = 'GET', body, query, headers, idempotencyKey, ifMatch, signal } = options

  let response: Response
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      credentials: 'include',
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
        ...(ifMatch ? { 'If-Match': ifMatch } : {}),
        ...headers,
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    })
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new ApiError({
      status: 0,
      errorCode: 'NETWORK_ERROR',
      message: 'Could not reach FenceAuth. Check your connection and try again.',
    })
  }

  // Expired session → one silent refresh, then a single retry (§5.1).
  if (response.status === 401 && !options._isRetry && !isAuthPath(path)) {
    const refreshed = await trySilentRefresh()
    if (refreshed) {
      return apiFetchWithMeta<T>(path, { ...options, _isRetry: true })
    }
  }

  const envelope = await parseEnvelope<T>(response)

  if (!response.ok || envelope.success === false) {
    const retryAfter = response.headers.get('Retry-After')
    throw new ApiError({
      status: response.status,
      errorCode: envelope.errorCode,
      message: envelope.message,
      errors: envelope.errors,
      retryAfterSeconds: retryAfter ? Number(retryAfter) : undefined,
    })
  }

  return {
    data: (envelope.data ?? null) as T,
    message: envelope.message ?? null,
    etag: response.headers.get('ETag'),
    status: response.status,
  }
}

/** Convenience variant returning only the unwrapped `data`. */
export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const result = await apiFetchWithMeta<T>(path, options)
  return result.data
}

function isAuthPath(path: string): boolean {
  return path.startsWith('/api/v1/auth/')
}

async function parseEnvelope<T>(response: Response): Promise<ApiEnvelope<T>> {
  if (response.status === 204) return { success: true, data: null }
  const text = await response.text()
  if (!text) {
    return { success: response.ok, data: null }
  }
  try {
    return JSON.parse(text) as ApiEnvelope<T>
  } catch {
    return {
      success: response.ok,
      data: null,
      message: response.ok ? null : 'Unexpected response from the server.',
    }
  }
}
